import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
  Typography,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  Divider,
  Tooltip,
  Menu,
  ListItemText,

} from "@mui/material";
import { Edit, Delete, Download } from "@mui/icons-material";
import { ViewColumn, DensityMedium, Search } from "@mui/icons-material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useTheme } from "@mui/material";
import { useEffect, useState, useMemo, useCallback } from "react";
import BrevetService from "../../api/services/BrevetService";
import { styled, alpha } from "@mui/material/styles";
import { CircularProgress } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Snackbar, Slide } from "@mui/material";
import { useLocation } from "react-router-dom";

const StyledCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha('#2a5fa9', 0.05)} 0%, ${alpha('#65b986', 0.05)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: '16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 20px 40px ${alpha('#2a5fa9', 0.1)}`,
    border: `1px solid ${alpha('#2a5fa9', 0.2)}`,
  },
  padding: '24px',
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: "none",
  borderRadius: "16px",
  background: `linear-gradient(135deg,
    ${alpha(theme.palette.background.paper, 0.9)} 0%,
    ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
  backdropFilter: "blur(10px)",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
  width: "100%",
  height: "100%",
  "& .MuiDataGrid-main": {
    overflow: "visible",
  },
  "& .MuiDataGrid-columnHeaders": {
    background: `linear-gradient(90deg,
      #2a5fa9 0%, #3a7fb5 20%, #4a9fc0 40%,
      #5ab8a0 60%, #70bd8d 80%, #8fc68b 100%)`,
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.875rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "none",
    minHeight: "56px",
    overflow: "visible",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
    padding: "16px",
    "&:nth-of-type(even)": {
      backgroundColor: alpha(theme.palette.action.hover, 0.02),
    },
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "none",
    background: `linear-gradient(135deg, #fcfdff 0%, #f6f9fa 100%)`,
    color: theme.palette.text.primary,
  },
  "& .MuiCheckbox-root": {
    color: `${theme.palette.primary.main} !important`,
  },
  "& .MuiDataGrid-virtualScroller": {
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: alpha(theme.palette.divider, 0.1),
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: alpha("#2a5fa9", 0.3),
      borderRadius: "4px",
      "&:hover": {
        background: alpha("#2a5fa9", 0.5),
      },
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
      padding: "8px",
      fontSize: "0.75rem",
    },
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: `linear-gradient(90deg, 
    #2a5fa9 0%, 
    #3a7fb5 20%,
    #4a9fc0 40%, 
    #5ab8a0 60%,
    #70bd8d 80%, 
    #8fc68b 100%)`,
  '& .MuiTableCell-head': {
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: 'none',
    padding: '20px 16px',
    whiteSpace: 'nowrap',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  padding: '16px',
  '&:nth-of-type(even)': {
    backgroundColor: alpha(theme.palette.action.hover, 0.02),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, #2a5fa9 0%, #65b986 100%)`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
  fontSize: '1.5rem',
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  background: alpha('#2a5fa9', 0.1),
  color: '#2a5fa9',
  '&:hover': {
    background: alpha('#2a5fa9', 0.2),
    transform: 'scale(1.1)',
  },
  '&:disabled': {
    background: alpha(theme.palette.action.disabled, 0.1),
    color: theme.palette.action.disabled,
  },
  transition: 'all 0.2s ease',
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const CustomToolbar = ({ setAnchorElColumns, setAnchorElDensity }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      p: 1, display: "flex", alignItems: "center",
      background: alpha(theme.palette.background.paper, 0.8),
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    }}>
      <Box display="flex" gap={1}>
        <Tooltip title="Colonnes">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton onClick={(e) => setAnchorElColumns(e.currentTarget)} size="small">
              <ViewColumn fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#000000', userSelect: 'none' }}>
              Colonnes
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Densité">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton onClick={(e) => setAnchorElDensity(e.currentTarget)} size="small">
              <DensityMedium fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#000000', userSelect: 'none' }}>
              Densité
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

const Brevets = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const userRole = localStorage.getItem("user_role");
  const token = localStorage.getItem("token");
  const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const [brevets, setBrevets] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [selectedBrevet, setSelectedBrevet] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [firstErrorField, setFirstErrorField] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [anchorElColumns, setAnchorElColumns] = useState(null);
  const [anchorElDensity, setAnchorElDensity] = useState(null);
  const [columnSearch, setColumnSearch] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [comptesComptables, setComptesComptables] = useState([]);
  const [loadingComptes, setLoadingComptes] = useState(false);
  const [comptesComptablesEdit, setComptesComptablesEdit] = useState([]);
  const [loadingComptesEdit, setLoadingComptesEdit] = useState(false);
  const [customCompteComptableAdd, setCustomCompteComptableAdd] = useState(false);
  const [customCompteComptableEdit, setCustomCompteComptableEdit] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    try {
      const saved = localStorage.getItem("contrats_column_visibility");
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const [density, setDensity] = useState(() => {
    return localStorage.getItem("contrats_density") || 'standard';
  });
  const [newBrevet, setNewBrevet] = useState({
    Code_Famille_CEGID: "",
    nom: "",
    descriptif: "",
    numero_brevet: "",
    numero_dossier: "",
    mandataire: "",
    pays: [],
    inventeur: "",
    titulaire: "",
    date_depot: "",
    date_publication: "",
    date_echeance_annuites: "",
    // date_de_protection: "",
    date_expiration: "",
    etat: "",
    debiteur_annuites: "",
    type_royalties: "",
    montant_royalties: "",
    frequence_royalties: "",
    ayant_droit: "",
    commentaires: "",
    compte_comptable: "",
    compte_comptable_id: "",
    compte_comptable_manuel: "",
    societe_id: "",
  });
  const [certificatDepotFile, setCertificatDepotFile] = useState(null);
  const [certificatEnregistrementFile, setCertificatEnregistrementFile] = useState(null);
  const [selectedCertificatDepotFile, setSelectedCertificatDepotFile] = useState(null);
  const [selectedCertificatEnregistrementFile, setSelectedCertificatEnregistrementFile] = useState(null);
  const [search, setSearch] = useState("");
  const [filterPays, setFilterPays] = useState("");
  const [filterEtat, setFilterEtat] = useState("");
  const [filterFrequence, setFilterFrequence] = useState("");
  const [error, setError] = useState("");
  const [countries, setCountries] = useState([]);
  const [inventeurs, setInventeurs] = useState([]);
  const [titulaires, setTitulaires] = useState([]);
  const [ayantsDroit, setAyantsDroit] = useState([]);
  const [debiteurs, setDebiteurs] = useState([]);
  const [newCountry, setNewCountry] = useState("");
  const [newInventeur, setNewInventeur] = useState("");
  const [newTitulaire, setNewTitulaire] = useState("");
  const [newDebiteur, setNewDebiteur] = useState("");
  const [newAyantDroit, setNewAyantDroit] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [societes, setSocietes] = useState([]);
  const [filterSociete, setFilterSociete] = useState("");

  const validateBrevet = (data) => {
    const errors = {};

    if (!data.Code_Famille_CEGID?.trim()) {
      errors.Code_Famille_CEGID = "Le code famille est requis.";
    }
    if (!data.societe_id || data.societe_id === "" || data.societe_id == null) {
      errors.societe_id = "La société est requise.";
    }

    if (!data.nom?.trim()) {
      errors.nom = "Le nom est requis.";
    }
    if (!data.compte_comptable_id && !data.compte_comptable_manuel?.trim()) {
      errors.compte_comptable_id = "Le compte comptable est requis.";
    }
    if (!data.pays || data.pays.length === 0) {
      errors.pays = "Au moins un pays doit être sélectionné.";
    }
    if (!data.date_depot?.trim()) {
      errors.date_depot = "La date de dépôt est requise.";
    }
    if (!data.date_expiration?.trim()) {
      errors.date_expiration = "La date d’expiration est requise.";
    }

    return errors;
  };

  const etatsBrevets = [
    'Actif',
    'Déchu',
    'Expiré',
    'Renouvelé',
    'Non Renouvelé',
    'Déposé',
    'A déposer',
    'Opposable',
    'Litige',
    'Publier'
  ];
  const royaltyTypes = ["% CA HT", "Somme sur les ventes"];
  const royaltyFrequencies = ["Trimestrielle", "Semestrielle", "Annuelle"];




  const handleNewDateDepotChange = (e) => {
    const depot = e.target.value;
    if (!depot) {
      setNewBrevet(prev => ({
        ...prev,
        date_depot: '',
        date_publication: '',
        date_expiration: '',
        // date_de_protection: '',
        date_echeance_annuites: ''
      }));
      return;
    }

    const protection = depot;
    const echeance = depot;

    // Calcul de la publication : +18 mois
    const dateDepot = new Date(depot);
    const publication = new Date(dateDepot);
    publication.setMonth(publication.getMonth() + 18);
    const pubStr = publication.toISOString().split('T')[0];

    // Calcul de l'expiration : +20 ans en préservant le jour exact
    const [year, month, day] = depot.split('-').map(Number);
    const expYear = year + 20;
    const expDateStr = `${expYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Vérifier si la date d'expiration est valide
    const expDate = new Date(expDateStr + 'T00:00:00');
    if (isNaN(expDate.getTime()) || expDate.getDate() !== day) {
      const lastDayOfMonth = new Date(expYear, month, 0);
      const validExpDateStr = `${expYear}-${String(month).padStart(2, '0')}-${String(lastDayOfMonth.getDate()).padStart(2, '0')}`;
      const expStr = validExpDateStr;

      setNewBrevet(prev => ({
        ...prev,
        date_depot: depot,
        // date_de_protection: protection,
        date_publication: pubStr,
        date_expiration: expStr,
        date_echeance_annuites: echeance
      }));
    } else {
      // La date est valide
      const expStr = expDateStr;

      setNewBrevet(prev => ({
        ...prev,
        date_depot: depot,
        // date_de_protection: protection,
        date_publication: pubStr,
        date_expiration: expStr,
        date_echeance_annuites: echeance
      }));
    }
  };

  const handleEditDateDepotChange = (e) => {
    const depot = e.target.value;
    if (!depot) {
      setSelectedBrevet(prev => ({
        ...prev,
        date_depot: '',
        date_publication: '',
        date_expiration: '',
        // date_de_protection: '',
        date_echeance_annuites: ''

      }));
      return;
    }

    const protection = depot;
    const echeance = depot;

    // Calcul de la publication : +18 mois
    const dateDepot = new Date(depot);
    const publication = new Date(dateDepot);
    publication.setMonth(publication.getMonth() + 18);
    const pubStr = publication.toISOString().split('T')[0];

    // Calcul de l'expiration : +20 ans en préservant le jour exact
    const [year, month, day] = depot.split('-').map(Number);
    const expYear = year + 20;
    const expDateStr = `${expYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Vérifier si la date d'expiration est valide
    const expDate = new Date(expDateStr + 'T00:00:00');
    if (isNaN(expDate.getTime()) || expDate.getDate() !== day) {
      // La date n'existe pas (ex: 31 avril), prendre le dernier jour du mois
      const lastDayOfMonth = new Date(expYear, month, 0);
      const validExpDateStr = `${expYear}-${String(month).padStart(2, '0')}-${String(lastDayOfMonth.getDate()).padStart(2, '0')}`;
      const expStr = validExpDateStr;

      setSelectedBrevet(prev => ({
        ...prev,
        date_depot: depot,
        // date_de_protection: protection,
        date_publication: pubStr,
        date_expiration: expStr,
        date_echeance_annuites: echeance
      }));
    } else {
      // La date est valide
      const expStr = expDateStr;

      setSelectedBrevet(prev => ({
        ...prev,
        date_depot: depot,
        // date_de_protection: protection,
        date_publication: pubStr,
        date_expiration: expStr,
        date_echeance_annuites: echeance
      }));
    }
  };

  const fetchBrevets = async () => {
    setIsLoading(true);
    try {
      const responseData = await BrevetService.getBrevet(userRole, token);
      const brevetsData = responseData.brevets.map((brevet) => {
        let paysValue = [];
        if (Array.isArray(brevet.pays)) {
          paysValue = brevet.pays;
        } else if (typeof brevet.pays === "string") {
          const trimmedPays = brevet.pays.trim();
          if (trimmedPays.startsWith('[') && trimmedPays.endsWith(']')) {
            try {
              paysValue = JSON.parse(trimmedPays);
            } catch (parseErr) {
              paysValue = [trimmedPays];
            }
          } else {
            paysValue = [trimmedPays];
          }
        }
        return {
          ...brevet,
          pays: paysValue,
          societe_id: brevet.societe_id,
          type_royalties: brevet.type_royalties || brevet.royalty_type || "",
          montant_royalties: brevet.montant_royalties || brevet.royalty_amount || "",
          frequence_royalties: brevet.frequence_royalties || brevet.royalty_frequency || "",
        };
      });
      setBrevets(brevetsData);
      const uniqueInventeurs = [...new Set(brevetsData.map(b => b.inventeur).filter(Boolean))].sort();
      setInventeurs(uniqueInventeurs);
      const uniqueTitulaires = [...new Set(brevetsData.map(b => b.titulaire).filter(Boolean))].sort();
      setTitulaires(uniqueTitulaires);
      const uniqueDebiteurs = [...new Set(brevetsData.map(b => b.debiteur_annuites).filter(Boolean))].sort();
      setDebiteurs(uniqueDebiteurs);
      const uniqueAyants = [...new Set(brevetsData.map(b => b.ayant_droit).filter(Boolean))].sort();
      setAyantsDroit(uniqueAyants);
      setCountries(responseData.countries || []);
      const societesList = responseData.societes || [];
      setSocietes(societesList);
      setError("");
    } catch (err) {
      console.error("Erreur lors de la récupération des brevets:", err);
      setError(
        err.response?.data?.message || "Erreur lors de la récupération des brevets."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const chargerComptesComptables = async (societeId, isEdit = false) => {
    if (!societeId) {
      isEdit ? setComptesComptablesEdit([]) : setComptesComptables([]);
      return;
    }
    isEdit ? setLoadingComptesEdit(true) : setLoadingComptes(true);
    try {
      const data = await BrevetService.getComptesComptables(userRole, token, societeId);
      isEdit ? setComptesComptablesEdit(data) : setComptesComptables(data);
    } catch (err) {
      setSnackbarMessage("Impossible de charger le plan comptable pour cette société.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      isEdit ? setComptesComptablesEdit([]) : setComptesComptables([]);
    } finally {
      isEdit ? setLoadingComptesEdit(false) : setLoadingComptes(false);
    }
  };

  useEffect(() => {
    fetchBrevets();
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(
        "contrats_column_visibility",
        JSON.stringify(columnVisibilityModel)
      );
    } catch (e) {
      console.error("Erreur sauvegarde visibilité colonnes :", e);
    }
  }, [columnVisibilityModel]);

  const handleAddCountry = () => {
    if (newCountry.trim() && !countries.includes(newCountry.trim().toUpperCase())) {
      setCountries([...countries, newCountry.trim().toUpperCase()]);
      setNewBrevet({ ...newBrevet, pays: [...newBrevet.pays, newCountry.trim().toUpperCase()] });
      setNewCountry("");
    }
  };

  const handleAddInventeur = () => {
    if (newInventeur.trim() && !inventeurs.includes(newInventeur.trim())) {
      setInventeurs([...inventeurs, newInventeur.trim()]);
      setNewBrevet({ ...newBrevet, inventeur: newInventeur.trim() });
      setNewInventeur("");
    }
  };

  const handleAddTitulaire = () => {
    if (newTitulaire.trim() && !titulaires.includes(newTitulaire.trim())) {
      setTitulaires([...titulaires, newTitulaire.trim()]);
      setNewBrevet({ ...newBrevet, titulaire: newTitulaire.trim() });
      setNewTitulaire("");
    }
  };

  const handleAddDebiteur = () => {
    if (newDebiteur.trim() && !debiteurs.includes(newDebiteur.trim())) {
      setDebiteurs([...debiteurs, newDebiteur.trim()]);
      setNewBrevet({ ...newBrevet, debiteur_annuites: newDebiteur.trim() });
      setNewDebiteur("");
    }
  };

  const handleAddAyantDroit = () => {
    if (newAyantDroit.trim() && !ayantsDroit.includes(newAyantDroit.trim())) {
      setAyantsDroit([...ayantsDroit, newAyantDroit.trim()]);
      setNewBrevet({ ...newBrevet, ayant_droit: newAyantDroit.trim() });
      setNewAyantDroit("");
    }
  };

  const toolbarComponent = useCallback(() => (
    <CustomToolbar
      setAnchorElColumns={setAnchorElColumns}
      setAnchorElDensity={setAnchorElDensity}
    />
  ), []);

  const handleDownloadExcel = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const timestamp = `${pad(now.getDate())}_${pad(now.getMonth() + 1)}_${now.getFullYear()}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
    const fileName = `brevets_${timestamp}.csv`;

    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");
    const fmtNum = (n) =>
      n !== null && n !== undefined && n !== "" ? String(n).replace(".", ",") : "";
    const fmtCompte = (cc) => {
      if (!cc) return "";
      if (typeof cc === "string") return cc;
      return `${cc.code} - ${cc.libelle}`;
    };
    const fmtPays = (p) => (Array.isArray(p) ? p.join(" ; ") : p || "");

    const headers = [
      "Société",
      "Code Famille",
      "Compte Comptable",
      "Nom",
      "Descriptif",
      "Numéro Brevet",
      "Numéro Dossier",
      "Mandataire",
      "Pays",
      "Inventeur",
      "Titulaire",
      "Débiteur Annuités",
      "Ayant Droit",
      "Date Dépôt",
      "Date Publication",
      "Date Échéance Annuités",
      "Date Expiration",
      "État",
      "Type Royalties",
      "Montant Royalties",
      "Fréquence Royalties",
      "Commentaires",
    ];

    const data = filteredRows.map((row) => [
      row.societe_nom || "",
      row.Code_Famille_CEGID || "",
      fmtCompte(row.compte_comptable),
      row.nom || "",
      row.descriptif || "",
      row.numero_brevet || "",
      row.numero_dossier || "",
      row.mandataire || "",
      fmtPays(row.pays),
      row.inventeur || "",
      row.titulaire || "",
      row.debiteur_annuites || "",
      row.ayant_droit || "",
      fmtDate(row.date_depot),
      fmtDate(row.date_publication),
      fmtDate(row.date_echeance_annuites),
      fmtDate(row.date_expiration),
      row.etat || "",
      row.type_royalties || "",
      fmtNum(row.montant_royalties),
      row.frequence_royalties || "",
      row.commentaires || "",
    ]);

    const csvContent =
      "\uFEFF" +
      [
        headers.map((h) => `"${h}"`).join(";"),
        ...data.map((row) =>
          row
            .map((cell) => `"${String(cell).replace(/"/g, '""').replace(/\n|\r/g, " ")}"`)
            .join(";")
        ),
      ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };


  const handleEditClick = (brevet) => {
    let paysValue = [];
    if (Array.isArray(brevet.pays)) {
      paysValue = brevet.pays;
    } else if (typeof brevet.pays === "string") {
      const trimmedPays = brevet.pays.trim();
      if (trimmedPays.startsWith('[') && trimmedPays.endsWith(']')) {
        try {
          paysValue = JSON.parse(trimmedPays);
        } catch (parseErr) {
          paysValue = [trimmedPays];
        }
      } else {
        paysValue = [trimmedPays];
      }
    }
    setSelectedBrevet({
      ...brevet,
      Code_Famille_CEGID: brevet.Code_Famille_CEGID || brevet.code_famille || "",
      date_depot: brevet.date_depot ? brevet.date_depot.split("T")[0] : "",
      date_publication: brevet.date_publication ? brevet.date_publication.split("T")[0] : "",
      date_echeance_annuites: brevet.date_echeance_annuites ? brevet.date_echeance_annuites.split("T")[0] : "",
      // date_de_protection: brevet.date_de_protection ? brevet.date_de_protection.split("T")[0] : "",
      date_expiration: brevet.date_expiration ? brevet.date_expiration.split("T")[0] : "",
      type_royalties: brevet.type_royalties || brevet.royalty_type || "",
      montant_royalties: brevet.montant_royalties || brevet.royalty_amount || "",
      frequence_royalties: brevet.frequence_royalties || brevet.royalty_frequency || "",
      pays: paysValue,
      inventeur: brevet.inventeur || "",
      titulaire: brevet.titulaire || "",
      debiteur_annuites: brevet.debiteur_annuites || "",
      ayant_droit: brevet.ayant_droit || "",
      societe_id: brevet.societe_id || "",
      compte_comptable_id: brevet.compte_comptable_id || "",
      compte_comptable_manuel: brevet.compte_comptable_id
        ? ""
        : (typeof brevet.compte_comptable === "string" ? brevet.compte_comptable : ""),
    });
    setNewCountry("");
    setNewInventeur("");
    setNewTitulaire("");
    setNewDebiteur("");
    setNewAyantDroit("");
    setSelectedCertificatDepotFile(null);
    setSelectedCertificatEnregistrementFile(null);
    setFormErrors({});
    setCustomCompteComptableEdit(
      !brevet.compte_comptable_id && typeof brevet.compte_comptable === "string" && brevet.compte_comptable.trim() !== ""
    );
    chargerComptesComptables(brevet.societe_id, true);
    setOpenEdit(true);
  };

  const handleDeleteClick = (brevet) => {
    setSelectedBrevet(brevet);
    setOpenDelete(true);
  };

  const handleAddClick = () => {
    setNewBrevet({
      Code_Famille_CEGID: "",
      nom: "",
      descriptif: "",
      numero_brevet: "",
      numero_dossier: "",
      mandataire: "",
      pays: [],
      inventeur: "",
      titulaire: "",
      date_depot: "",
      date_publication: "",
      date_echeance_annuites: "",
      // date_de_protection: "",
      date_expiration: "",
      etat: "",
      debiteur_annuites: "",
      type_royalties: "",
      montant_royalties: "",
      frequence_royalties: "",
      ayant_droit: "",
      commentaires: "",
      compte_comptable: "",
      compte_comptable_id: "",
      compte_comptable_manuel: "",
      societe_id: "",
    });
    setComptesComptables([]);
    setCustomCompteComptableAdd(false);
    setCertificatDepotFile(null);
    setCertificatEnregistrementFile(null);
    setNewCountry("");
    setNewInventeur("");
    setNewTitulaire("");
    setNewDebiteur("");
    setNewAyantDroit("");
    setFormErrors({});
    setOpenAdd(true);
  };

  const handleAddSubmit = async () => {
    const errors = validateBrevet(newBrevet);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      setFirstErrorField(firstErrorKey);
      setSnackbarMessage(errors[firstErrorKey]);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const formData = new FormData();
    const paysArray = Array.isArray(newBrevet.pays) ? newBrevet.pays : [];
    paysArray.forEach((pays, index) => formData.append(`pays[${index}]`, pays));

    Object.entries(newBrevet).forEach(([key, value]) => {
      if (
        key === "pays" ||
        key === "annuite" ||
        key === "date_de_protection" ||
        key === "compte_comptable" ||
        key === "compte_comptable_id" ||
        key === "compte_comptable_manuel"
      ) return;
      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });

    if (newBrevet.compte_comptable_id) {
      formData.append("compte_comptable_id", newBrevet.compte_comptable_id);
    } else {
      formData.append("compte_comptable", newBrevet.compte_comptable_manuel || "");
    }

    if (certificatDepotFile) formData.append("certificat_depot_pdf", certificatDepotFile);
    if (certificatEnregistrementFile) formData.append("certificat_enregistrement_pdf", certificatEnregistrementFile);

    try {
      await BrevetService.addBrevet(userRole, token, formData);
      setOpenAdd(false);
      setSnackbarMessage("Brevet ajouté avec succès !");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      await fetchBrevets();
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Erreur lors de l'ajout.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleEditSubmit = async () => {
    const errors = validateBrevet(selectedBrevet);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      const firstErrorKey = Object.keys(errors)[0];
      setFirstErrorField(firstErrorKey);
      setSnackbarMessage(errors[firstErrorKey]);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const formData = new FormData();
    const paysArray = Array.isArray(selectedBrevet.pays) ? selectedBrevet.pays : [];
    paysArray.forEach((pays, index) => formData.append(`pays[${index}]`, pays));

    Object.entries(selectedBrevet).forEach(([key, value]) => {
      //  Exclure aussi les champs PDF qui seront gérés séparément
      if (
        key === "pays" ||
        key === "id" ||
        key === "annuite" ||
        key === "date_de_protection" ||
        key === "certificat_depot_pdf" ||
        key === "certificat_enregistrement_pdf" ||
        key === "compte_comptable" ||
        key === "compte_comptable_id" ||
        key === "compte_comptable_manuel"
      ) return;

      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });

    if (selectedBrevet.compte_comptable_id) {
      formData.append("compte_comptable_id", selectedBrevet.compte_comptable_id);
    } else {
      formData.append("compte_comptable", selectedBrevet.compte_comptable_manuel || "");
    }

    //  N'envoyer les PDF QUE si un nouveau fichier a été sélectionné
    if (selectedCertificatDepotFile instanceof File) {
      formData.append("certificat_depot_pdf", selectedCertificatDepotFile);
    }

    if (selectedCertificatEnregistrementFile instanceof File) {
      formData.append("certificat_enregistrement_pdf", selectedCertificatEnregistrementFile);
    }

    formData.append("_method", "PUT");

    try {
      await BrevetService.updateBrevet(userRole, token, selectedBrevet.id, formData);
      setOpenEdit(false);
      setSnackbarMessage("Brevet modifié avec succès !");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      await fetchBrevets();
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Erreur lors de la modification.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBrevet?.id) return;

    try {
      await BrevetService.deleteBrevet(userRole, token, selectedBrevet.id);
      setOpenDelete(false);
      setSnackbarMessage("Brevet supprimé avec succès !");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      await fetchBrevets();
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || "Erreur lors de la suppression.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const location = useLocation();

  // Highlight de la ligne venant des alertes
  const [highlightId, setHighlightId] = useState(null);
  const [filterFromAlerte, setFilterFromAlerte] = useState(false);
  const [alerteFilterNom, setAlerteFilterNom] = useState("");
  // Highlight + Filtre depuis les alertes
  useEffect(() => {
    if (!location.state?.highlightId || brevets.length === 0) return;

    const id = location.state.highlightId;
    const nomBrevet = location.state.highlightItem;   //le nom du brevet

    // Réinitialiser les filtres manuels
    setSearch("");
    setFilterPays("");
    setFilterEtat("");
    setFilterFrequence("");
    setFilterSociete("");

    if (nomBrevet) {
      // Mode filtre alerte
      setAlerteFilterNom(nomBrevet);
      setFilterFromAlerte(true);
      setHighlightId(id);
    } else {
      setHighlightId(id);
      setFilterFromAlerte(false);
    }
  }, [location.state, brevets]);



  const PaysSelector = ({ value, onChange, countries, onAddCountry, newCountry, onNewCountryChange, error, helperText }) => (
    <Box>
      <FormControl fullWidth margin="dense" error={error}>
        <InputLabel id="pays-select-label">Pays *</InputLabel>
        <Select
          labelId="pays-select-label"
          id="pays-select"
          multiple
          value={value || []}
          onChange={(event) => onChange(event.target.value)}
          input={<OutlinedInput id="select-pays" label="Pays *" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
          MenuProps={MenuProps}
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            borderRadius: '12px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px'
            }
          }}
        >
          {countries.map((country) => (
            <MenuItem key={country} value={country}>
              {country}
            </MenuItem>
          ))}
        </Select>
        {helperText && <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>{helperText}</Typography>}
      </FormControl>
      <Box display="flex" alignItems="center" mt={1}>
        <TextField
          label="Nouveau pays"
          value={newCountry}
          onChange={(e) => onNewCountryChange(e.target.value)}
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            borderRadius: '12px',
            mr: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px'
            }
          }}
        />
        <Button
          variant="contained"
          onClick={onAddCountry}
          disabled={!newCountry.trim()}
          sx={{ borderRadius: '12px' }}
        >
          Ajouter
        </Button>
      </Box>
    </Box>
  );

  const columns = [
    // { field: "id", headerName: "ID", width: 60 },
    {
      field: "compte_comptable",
      headerName: "Compte Comptable",
      flex: 2,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const cc = params.row.compte_comptable;
        if (!cc) return "-";
        if (typeof cc === "string") return cc;
        return `${cc.code} - ${cc.libelle}`;
      },
    },
    { field: "Code_Famille_CEGID", headerName: "Code Famille", width: 120, sortable: false, },
    { field: "nom", headerName: "Nom", width: 120, sortable: false, },
    { field: "numero_brevet", headerName: "N° Brevet", width: 120, sortable: false, },
    {
      field: "pays",
      headerName: "Pays",
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {Array.isArray(params.value)
            ? params.value.join(", ")
            : params.value || "-"}
        </Typography>
      ),
      sortable: false,
    },
    {
      field: "date_depot",
      headerName: "Date Dépôt",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
        </Typography>
      ),
      sortable: true,
    },
    {
      field: "date_expiration",
      headerName: "Date Expiration",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR") : "-"}
        </Typography>
      ),
      sortable: true,
    },
    { field: "etat", headerName: "État", width: 150, sortable: false, },
    {
      field: "montant_royalties",
      headerName: "Montant Royalties",
      flex: 2,
      minWidth: 170,
      sortable: false,
    },
    {
      field: "frequence_royalties",
      headerName: "Fréquence Royalties",
      width: 170,
      sortable: false,
    },
    {
      field: "societe_nom",
      headerName: "Société",
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.row.societe_nom || "—"}
        </Typography>
      ),
    },
    {
      field: "certificat_depot_pdf",
      headerName: "Cert. Dépôt PDF",
      width: 150,
      renderCell: (params) =>
        params.row.certificat_depot_pdf ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={() =>
              window.open(
                `${process.env.REACT_APP_API_URL}/storage/certificats/${params.row.certificat_depot_pdf.split('/').pop()}`,
                '_blank'
              )
            }
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.3),
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main,
              },
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
            }}
          >
            Télécharger
          </Button>
        ) : (
          <Typography variant="body2" color="textSecondary">-</Typography>
        ),
      sortable: false,
    },
    {
      field: "certificat_enregistrement_pdf",
      headerName: "Cert. Enreg. PDF",
      width: 150,
      renderCell: (params) =>
        params.row.certificat_enregistrement_pdf ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={() =>
              window.open(
                `${process.env.REACT_APP_API_URL}/storage/certificats/${params.row.certificat_enregistrement_pdf.split('/').pop()}`,
                '_blank'
              )
            }
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.3),
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main,
              },
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
            }}
          >
            Télécharger
          </Button>
        ) : (
          <Typography variant="body2" color="textSecondary">-</Typography>
        ),
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <StyledIconButton
            onClick={() => handleEditClick(params.row)}
            size="small"
            title="Modifier"
          >
            <Edit fontSize="small" />
          </StyledIconButton>
          <StyledIconButton
            onClick={() => handleDeleteClick(params.row)}
            size="small"
            title="Supprimer"
            sx={{ color: theme.palette.error.main }}
          >
            <Delete fontSize="small" />
          </StyledIconButton>
        </Box>
      ),
      sortable: false,
    },
  ];

  const filteredRows = useMemo(() => {
    return brevets.filter((b) => {
      const searchLower = search.toLowerCase();

      const matchSearch =
        b.nom?.toLowerCase().includes(searchLower) ||
        b.Code_Famille_CEGID?.toLowerCase().includes(searchLower) ||
        b.numero_brevet?.toLowerCase().includes(searchLower) ||
        (Array.isArray(b.pays) ? b.pays.some(p => p.toLowerCase().includes(searchLower)) : b.pays?.toLowerCase().includes(searchLower)) ||
        b.etat?.toLowerCase().includes(searchLower) ||
        b.societe_nom?.toLowerCase().includes(searchLower);

      const matchPays = filterPays
        ? (Array.isArray(b.pays) ? b.pays.includes(filterPays) : b.pays === filterPays)
        : true;

      const matchEtat = filterEtat ? b.etat === filterEtat : true;
      const matchFrequence = filterFrequence ? b.frequence_royalties === filterFrequence : true;
      const matchSociete = filterSociete !== ""
        ? b.societe_id === parseInt(filterSociete, 10)
        : true;

      // Filtre prioritaire venant d'une alerte
      const matchAlerte = filterFromAlerte && alerteFilterNom
        ? b.nom?.toLowerCase().trim() === alerteFilterNom.toLowerCase().trim()
        : true;

      return matchSearch && matchPays && matchEtat && matchFrequence && matchSociete && matchAlerte;
    });
  }, [brevets, search, filterPays, filterEtat, filterFrequence, filterSociete, filterFromAlerte, alerteFilterNom]);

  useEffect(() => {
    if (firstErrorField) {
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        element.focus({ preventScroll: true });
      }
    }
  }, [firstErrorField]);
  return (
    <Box

      sx={{
        m: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Header
        title="BREVETS"
        subtitle={userRole === "gestionnaire" ? "Gérer vos brevets" : "Gestion centralisée des brevets"}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <StyledCard>
        <Box
          mb={2}
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
        >
          {/* Recherche texte */}
          <TextField
            label="Rechercher..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              borderRadius: '8px',
              flex: 1,
              minWidth: 220,
            }}
          />

          {/* Filtre Société — visible pour gestionnaire ET admin */}
          {(userRole === "gestionnaire" || userRole === "admin") && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="filter-societe-label">Société</InputLabel>
              <Select
                labelId="filter-societe-label"
                value={filterSociete}
                label="Société"
                onChange={(e) => setFilterSociete(e.target.value)}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  borderRadius: '8px',
                  minWidth: 180,
                }}
              >
                <MenuItem value="">Toutes mes sociétés</MenuItem>
                {societes.map((soc) => (
                  <MenuItem key={soc.id} value={soc.id}>
                    {soc.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}


          <TextField
            select
            label="Pays"
            variant="outlined"
            size="small"
            value={filterPays}
            onChange={(e) => setFilterPays(e.target.value)}
            sx={{
              minWidth: { xs: '100%', sm: 150 },
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              borderRadius: '8px',
            }}
          >
            <MenuItem value="">Tous</MenuItem>
            {countries.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="État"
            variant="outlined"
            size="small"
            value={filterEtat}
            onChange={(e) => setFilterEtat(e.target.value)}
            sx={{
              minWidth: { xs: '100%', sm: 150 },
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              borderRadius: '8px',
            }}
          >
            <MenuItem value="">Tous</MenuItem>
            {etatsBrevets.map((e) => (
              <MenuItem key={e} value={e}>
                {e}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Fréquence"
            variant="outlined"
            size="small"
            value={filterFrequence}
            onChange={(e) => setFilterFrequence(e.target.value)}
            sx={{
              minWidth: { xs: '100%', sm: 170 },
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              borderRadius: '8px',
            }}
          >
            <MenuItem value="">Tous</MenuItem>
            {royaltyFrequencies.map((freq) => (
              <MenuItem key={freq} value={freq}>
                {freq}
              </MenuItem>
            ))}
          </TextField>

          {(userRole === "gestionnaire" || userRole === "admin") && (
            <Button
              variant="contained"
              onClick={handleAddClick}
              sx={{
                borderRadius: '8px',
                background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`,
                color: '#ffffff',
                '&:hover': {
                  background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`,
                  transform: 'scale(1.05)'
                },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Ajouter un brevet
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<Download />}
            onClick={handleDownloadExcel}
            sx={{
              borderRadius: '8px',
              background: `linear-gradient(135deg, #2a5fa9 0%, #4cbed8 100%)`,
              color: '#ffffff',
              '&:hover': {
                background: `linear-gradient(135deg, #4cbed8 0%, #65b986 100%)`,
                transform: 'scale(1.05)'
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Télécharger Excel
          </Button>
        </Box>
        {/* MENU COLONNES */}
        <Menu
          anchorEl={anchorElColumns}
          open={Boolean(anchorElColumns)}
          onClose={() => setAnchorElColumns(null)}
          disableAutoFocusItem
          keepMounted
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: { maxHeight: 500, width: 320, '& .MuiList-root': { p: 0 } },
          }}
        >
          {/* Recherche */}
          <Box sx={{ px: 1, pt: 1 }}>
            <TextField
              fullWidth
              placeholder="Rechercher une colonne"
              size="small"
              value={columnSearch}
              onChange={(e) => setColumnSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: <Search fontSize="small" sx={{ mr: 0.5 }} />,
              }}
            />
          </Box>

          {/* Liste des colonnes */}
          <Box sx={{ maxHeight: 320, overflowY: 'auto', px: 1, py: 1 }}>
            {[
              { field: "Code_Famille_CEGID", headerName: "Code Famille" },
              { field: "compte_comptable", headerName: "Compte Comptable" },
              { field: "nom", headerName: "Nom" },
              { field: "numero_brevet", headerName: "N° Brevet" },
              { field: "pays", headerName: "Pays" },
              { field: "date_depot", headerName: "Date Dépôt" },
              { field: "date_expiration", headerName: "Date Expiration" },
              { field: "etat", headerName: "État" },
              { field: "montant_royalties", headerName: "Montant Royalties" },
              { field: "frequence_royalties", headerName: "Fréquence Royalties" },
              { field: "certificat_depot_pdf", headerName: "Cert. Dépôt PDF" },
              { field: "certificat_enregistrement_pdf", headerName: "Cert. Enreg. PDF" },
              { field: "societe_nom", headerName: "Société" },
              { field: "actions", headerName: "Actions" },
            ]
              .filter(col =>
                col.headerName.toLowerCase().includes(columnSearch.toLowerCase())
              )
              .map((col) => (
                <Box
                  key={col.field}
                  onClick={() => {
                    setColumnVisibilityModel((prev) => ({
                      ...prev,
                      [col.field]: !(prev[col.field] ?? true),
                    }));
                  }}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderRadius: 1,
                    mb: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {/* Switch */}
                  <Box
                    sx={{
                      width: 34,
                      height: 20,
                      borderRadius: '10px',
                      backgroundColor:
                        (columnVisibilityModel[col.field] ?? true)
                          ? '#1976d2'
                          : '#e0e0e0',
                      position: 'relative',
                      mr: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        position: 'absolute',
                        top: '2px',
                        left:
                          (columnVisibilityModel[col.field] ?? true)
                            ? '16px'
                            : '2px',
                      }}
                    />
                  </Box>

                  <Typography sx={{ fontSize: '0.875rem' }}>
                    {col.headerName}
                  </Typography>
                </Box>
              ))}
          </Box>

          <Divider sx={{ my: 0.5 }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5 }}>
            <Button
              size="small"
              onClick={() => {
                const allHidden = {
                  Code_Famille_CEGID: false,
                  compte_comptable: false,
                  nom: false,
                  numero_brevet: false,
                  pays: false,
                  date_depot: false,
                  date_expiration: false,
                  etat: false,
                  montant_royalties: false,
                  frequence_royalties: false,
                  certificat_depot_pdf: false,
                  certificat_enregistrement_pdf: false,
                  societe_nom: false,
                  actions: false,
                };
                setColumnVisibilityModel(allHidden);
              }}
            >
              MASQUER TOUT
            </Button>

            <Button
              size="small"
              onClick={() => setColumnVisibilityModel({})}
            >
              AFFICHER TOUT
            </Button>
          </Box>
        </Menu>

        {/* MENU DENSITÉ */}
        <Menu
          anchorEl={anchorElDensity}
          open={Boolean(anchorElDensity)}
          onClose={() => setAnchorElDensity(null)}
        >
          <MenuItem onClick={() => { setDensity('compact'); setAnchorElDensity(null); }}>Compact</MenuItem>
          <MenuItem onClick={() => { setDensity('standard'); setAnchorElDensity(null); }}>Standard</MenuItem>
          <MenuItem onClick={() => { setDensity('comfortable'); setAnchorElDensity(null); }}>Confortable</MenuItem>
        </Menu>
        {/* === FILTRE ACTIF DEPUIS ALERTE === */}
        {filterFromAlerte && alerteFilterNom && (
          <Alert
            severity="info"
            icon={<Search />}
            sx={{ mb: 2, borderRadius: "12px" }}
            action={
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => {
                  setFilterFromAlerte(false);
                  setAlerteFilterNom("");
                  setHighlightId(null);
                }}
              >
                Afficher tous les brevets
              </Button>
            }
          >
            Affichage filtré pour le brevet : <strong>{alerteFilterNom}</strong>
          </Alert>
        )}
        <Box
          sx={{
            height: "70vh",
            width: "100%",
            minHeight: 600,
            overflow: "hidden",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <CircularProgress size={60} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Chargement des brevets...
              </Typography>
            </Box>
          ) : filteredRows.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Aucun brevet trouvé.
              </Typography>
            </Box>
          ) : (
            <StyledDataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => row.id}
              pagination
              paginationMode="client"
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 20, page: 0 },
                },
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              loading={isLoading}
              disableSelectionOnClick
              autoHeight={false}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
              disableColumnSorting={true}
              disableColumnMenu={true}
              density={density}
              getRowClassName={(params) =>
                params.id === highlightId ? "highlighted-row" : ""
              }
              components={{
                Toolbar: toolbarComponent
              }}
              localeText={{
                toolbarColumns: "Colonnes",
                toolbarDensity: "Densité",
                toolbarExport: "Exporter",
                columnsPanelShowAllButton: "Afficher tout",
                columnsPanelHideAllButton: "Masquer tout",
                noRowsLabel: "Aucun brevet",
              }}
              sx={{
                width: "100% !important",
                height: "100% !important",
                "& .MuiDataGrid-columnHeader": {
                  "& .MuiDataGrid-columnSeparator": { visibility: "visible" }
                },
                "& .MuiDataGrid-virtualScroller": {
                  minHeight: "300px",
                },
                "& .MuiDataGrid-sortIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-sortIcon": {
                  display: "none !important",
                },
                "& .highlighted-row": {
                  backgroundColor: `${alpha("#f59e0b", 0.25)} !important`,
                  outline: `2px solid #f59e0b`,
                  outlineOffset: "-2px",
                  transition: "background-color 0.5s ease",
                },

                [theme.breakpoints.down("sm")]: { fontSize: "0.75rem" },

              }}
            />
          )}
        </Box>

      </StyledCard>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={Slide}
        sx={{
          mt: 10,
          zIndex: 9999,
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            minWidth: 320,
            fontWeight: 600,
            fontSize: "0.95rem",
            boxShadow: `0 4px 20px ${alpha(
              snackbarSeverity === "error"
                ? theme.palette.error.main
                : theme.palette.success.main,
              0.3
            )}`,
            borderRadius: "12px",
            py: 1.5,
            background:
              snackbarSeverity === "success"
                ? `linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)`
                : snackbarSeverity === "error"
                  ? `linear-gradient(135deg, #f44336 0%, #ef5350 100%)`
                  : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Dialog d'ajout */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(
              theme.palette.background.paper,
              0.95
            )} 100%)`,
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <SectionTitle>Ajouter un Brevet</SectionTitle>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Remplissez les informations du nouveau brevet
          </Typography>
        </DialogTitle>

        <Divider sx={{ mb: 2 }} />

        <DialogContent sx={{ pt: 2 }}>
          {/* Error Message */}
          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Section: Informations Générales */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📋</span> Informations Générales
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                id="Code_Famille_CEGID"
                label="Code Famille"
                fullWidth
                required
                value={newBrevet.Code_Famille_CEGID}
                onChange={(e) => setNewBrevet({ ...newBrevet, Code_Famille_CEGID: e.target.value })}
                // error={!!formErrors.Code_Famille_CEGID}
                // helperText={formErrors.Code_Famille_CEGID || " "}
                sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
              />
              <FormControl fullWidth error={!!formErrors.societe_id}>
                <InputLabel id="societe-add-label">Société *</InputLabel>
                <Select
                  labelId="societe-add-label"
                  value={newBrevet.societe_id || ""}
                  onChange={(e) => {
                    const societeId = e.target.value;
                    setNewBrevet({ ...newBrevet, societe_id: societeId, compte_comptable_id: "" });
                    chargerComptesComptables(societeId, false);
                  }} label="Société *"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': { borderRadius: "12px" }
                  }}
                >
                  <MenuItem value="">
                    <em>Choisir une société</em>
                  </MenuItem>
                  {societes.map((soc) => (
                    <MenuItem key={soc.id} value={soc.id}>
                      {soc.nom}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.societe_id && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {formErrors.societe_id}
                  </Typography>
                )}
              </FormControl>
              <TextField
                id="nom"
                label="Nom"
                fullWidth
                required
                value={newBrevet.nom}
                onChange={(e) => setNewBrevet({ ...newBrevet, nom: e.target.value })}
                // error={!!formErrors.nom}
                // helperText={formErrors.nom || " "}
                sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
              />
              {!customCompteComptableAdd ? (
                <FormControl
                  fullWidth
                  disabled={!newBrevet.societe_id || loadingComptes}
                  error={!!formErrors.compte_comptable_id}
                >
                  <InputLabel id="compte-comptable-label">Compte Comptable *</InputLabel>
                  <Select
                    labelId="compte-comptable-label"
                    value={newBrevet.compte_comptable_id || ""}
                    onChange={(e) => setNewBrevet({ ...newBrevet, compte_comptable_id: e.target.value })}
                    label="Compte Comptable *"
                    sx={{
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      '& .MuiOutlinedInput-root': { borderRadius: "12px" }
                    }}
                  >
                    <MenuItem value="" disabled>
                      {loadingComptes ? "Chargement..." : "Choisir un compte"}
                    </MenuItem>
                    {comptesComptables.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.code} — {c.libelle}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomCompteComptableAdd(true);
                        setNewBrevet({ ...newBrevet, compte_comptable_id: "" });
                      }}
                      sx={{
                        mx: 0.5, borderRadius: '8px',
                        color: '#2a5fa9', fontWeight: 600, fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 1,
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #2a5fa9, #4cbed8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      }}>+</Box>
                      Saisir manuellement
                    </MenuItem>
                  </Select>
                  {!newBrevet.societe_id && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Sélectionnez d'abord une société.
                    </Typography>
                  )}
                  {formErrors.compte_comptable_id && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formErrors.compte_comptable_id}
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <TextField
                  label="Compte Comptable (saisie manuelle) *"
                  fullWidth
                  autoFocus
                  value={newBrevet.compte_comptable_manuel || ""}
                  onChange={(e) => setNewBrevet({ ...newBrevet, compte_comptable_manuel: e.target.value })}
                  sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => { setCustomCompteComptableAdd(false); setNewBrevet({ ...newBrevet, compte_comptable_manuel: "" }); }}
                        sx={{ color: "error.main", minWidth: 'auto', fontSize: '0.75rem' }}
                      >
                        ✕
                      </Button>
                    )
                  }}
                />
              )}
              <TextField
                label="Descriptif"
                fullWidth
                multiline
                rows={2}
                value={newBrevet.descriptif}
                onChange={(e) => setNewBrevet({ ...newBrevet, descriptif: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
              <TextField
                label="Numéro Brevet"
                fullWidth
                value={newBrevet.numero_brevet}
                onChange={(e) => setNewBrevet({ ...newBrevet, numero_brevet: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
              <TextField
                label="Numéro Dossier"
                fullWidth
                value={newBrevet.numero_dossier}
                onChange={(e) => setNewBrevet({ ...newBrevet, numero_dossier: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Parties Prenantes */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>👥</span> Parties Prenantes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Mandataire"
                fullWidth
                value={newBrevet.mandataire}
                onChange={(e) => setNewBrevet({ ...newBrevet, mandataire: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
              <FormControl fullWidth>
                <InputLabel id="inventeur-label">Inventeur</InputLabel>
                <Select
                  labelId="inventeur-label"
                  value={newBrevet.inventeur || ""}
                  onChange={(e) => setNewBrevet({ ...newBrevet, inventeur: e.target.value })}
                  label="Inventeur"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value=""><em>Sélectionner un inventeur</em></MenuItem>
                  {inventeurs.map((inv) => (
                    <MenuItem key={inv} value={inv}>{inv}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Nouvel inventeur"
                  fullWidth
                  value={newInventeur}
                  onChange={(e) => setNewInventeur(e.target.value)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                  }}
                />
                <Button variant="contained" onClick={handleAddInventeur} disabled={!newInventeur.trim()} sx={{ borderRadius: "12px" }}>
                  Ajouter
                </Button>
              </Box>

              <FormControl fullWidth>
                <InputLabel id="titulaire-label">Titulaire</InputLabel>
                <Select
                  labelId="titulaire-label"
                  value={newBrevet.titulaire || ""}
                  onChange={(e) => setNewBrevet({ ...newBrevet, titulaire: e.target.value })}
                  label="Titulaire"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value=""><em>Sélectionner un titulaire</em></MenuItem>
                  {titulaires.map((tit) => (
                    <MenuItem key={tit} value={tit}>{tit}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Nouveau titulaire"
                  fullWidth
                  value={newTitulaire}
                  onChange={(e) => setNewTitulaire(e.target.value)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                  }}
                />
                <Button variant="contained" onClick={handleAddTitulaire} disabled={!newTitulaire.trim()} sx={{ borderRadius: "12px" }}>
                  Ajouter
                </Button>
              </Box>

              <FormControl fullWidth>
                <InputLabel id="debiteur-annuites-label">Débiteur Annuités</InputLabel>
                <Select
                  labelId="debiteur-annuites-label"
                  value={newBrevet.debiteur_annuites || ""}
                  onChange={(e) => setNewBrevet({ ...newBrevet, debiteur_annuites: e.target.value })}
                  label="Débiteur Annuités"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value=""><em>Sélectionner un débiteur</em></MenuItem>
                  {debiteurs.map((deb) => (
                    <MenuItem key={deb} value={deb}>{deb}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Nouveau débiteur"
                  fullWidth
                  value={newDebiteur}
                  onChange={(e) => setNewDebiteur(e.target.value)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                  }}
                />
                <Button variant="contained" onClick={handleAddDebiteur} disabled={!newDebiteur.trim()} sx={{ borderRadius: "12px" }}>
                  Ajouter
                </Button>
              </Box>

              <FormControl fullWidth>
                <InputLabel id="ayant-droit-label">Ayant Droit</InputLabel>
                <Select
                  labelId="ayant-droit-label"
                  value={newBrevet.ayant_droit || ""}
                  onChange={(e) => setNewBrevet({ ...newBrevet, ayant_droit: e.target.value })}
                  label="Ayant Droit"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value=""><em>Sélectionner un ayant droit</em></MenuItem>
                  {ayantsDroit.map((ayant) => (
                    <MenuItem key={ayant} value={ayant}>{ayant}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Nouvel ayant droit"
                  fullWidth
                  value={newAyantDroit}
                  onChange={(e) => setNewAyantDroit(e.target.value)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                  }}
                />
                <Button variant="contained" onClick={handleAddAyantDroit} disabled={!newAyantDroit.trim()} sx={{ borderRadius: "12px" }}>
                  Ajouter
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Zone de Protection - Utilisation du composant PaysSelector */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🏷️</span> Zone de Protection
            </Typography>
            <PaysSelector
              value={newBrevet.pays || []}
              onChange={(newValue) => setNewBrevet({ ...newBrevet, pays: newValue })}
              countries={countries}
              onAddCountry={handleAddCountry}
              newCountry={newCountry}
              onNewCountryChange={setNewCountry}
              error={!!formErrors.pays}
              helperText={formErrors.pays}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Royalties */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>💰</span> Royalties
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControl fullWidth>
                <InputLabel id="type-royalties-label">Type de Royalties</InputLabel>
                <Select
                  labelId="type-royalties-label"
                  value={newBrevet.type_royalties || ""}
                  onChange={(e) => setNewBrevet({ ...newBrevet, type_royalties: e.target.value })}
                  label="Type de Royalties"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value=""><em>Sélectionner un type</em></MenuItem>
                  {royaltyTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Montant des Royalties"
                type="number"
                fullWidth
                value={newBrevet.montant_royalties}
                onChange={(e) => setNewBrevet({ ...newBrevet, montant_royalties: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
              <FormControl fullWidth>
                <InputLabel id="frequence-royalties-label">Fréquence des Royalties</InputLabel>
                <Select
                  labelId="frequence-royalties-label"
                  value={newBrevet.frequence_royalties || ""}
                  onChange={(e) => setNewBrevet({ ...newBrevet, frequence_royalties: e.target.value })}
                  label="Fréquence des Royalties"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value=""><em>Sélectionner une fréquence</em></MenuItem>
                  {royaltyFrequencies.map((freq) => (
                    <MenuItem key={freq} value={freq}>{freq}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Documents */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📄</span> Documents
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Certificat Dépôt PDF"
                type="file"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "application/pdf" }}
                onChange={(e) => setCertificatDepotFile(e.target.files[0] || null)}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
              <TextField
                label="Certificat Enregistrement PDF"
                type="file"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "application/pdf" }}
                onChange={(e) => setCertificatEnregistrementFile(e.target.files[0] || null)}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Dates */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📅</span> Dates Importantes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                id="date_depot"
                label="Date Dépôt"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={newBrevet.date_depot}
                onChange={handleNewDateDepotChange}
                // error={!!formErrors.date_depot}
                // helperText={formErrors.date_depot || " "}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
              <TextField
                label="Date Publication"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newBrevet.date_publication}
                onChange={(e) => setNewBrevet({ ...newBrevet, date_publication: e.target.value })}
                helperText="Auto-calculé : Dépôt + 18 mois"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
              <TextField
                label="Date Échéance Annuités"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newBrevet.date_echeance_annuites}
                onChange={(e) => setNewBrevet({ ...newBrevet, date_echeance_annuites: e.target.value })}
                helperText="Anniversaire annuel du dépôt"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
              {/* <TextField
                label="Date Protection"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newBrevet.date_de_protection}
                onChange={(e) => setNewBrevet({ ...newBrevet, date_de_protection: e.target.value })}
                helperText="Égale à la date de dépôt"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              /> */}
              <TextField
                label="Échéance"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newBrevet.date_expiration}
                onChange={(e) => setNewBrevet({ ...newBrevet, date_expiration: e.target.value })}
                helperText="Auto-calculé : Dépôt + 20 ans"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Informations Complémentaires */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>ℹ️</span> Informations Complémentaires
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                select
                label="État"
                fullWidth
                value={newBrevet.etat || ""}
                onChange={(e) => setNewBrevet({ ...newBrevet, etat: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              >
                {etatsBrevets.map((etat) => (
                  <MenuItem key={etat} value={etat}>
                    {etat}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Commentaires"
                fullWidth
                multiline
                rows={3}
                value={newBrevet.commentaires}
                onChange={(e) => setNewBrevet({ ...newBrevet, commentaires: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': { borderRadius: "12px" },
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider sx={{ mt: 2 }} />

        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => {
              setOpenAdd(false);
              setNewBrevet({
                Code_Famille_CEGID: "",
                nom: "",
                compte_comptable: "",
                descriptif: "",
                numero_brevet: "",
                numero_dossier: "",
                mandataire: "",
                pays: [],
                inventeur: "",
                titulaire: "",
                debiteur_annuites: "",
                ayant_droit: "",
                type_royalties: "",
                montant_royalties: "",
                frequence_royalties: "",
                date_depot: "",
                date_publication: "",
                date_echeance_annuites: "",
                date_expiration: "",
                etat: "",
                commentaires: "",
                societe_id: "",
              });
              setCertificatDepotFile(null);
              setCertificatEnregistrementFile(null);
              setNewCountry("");
              setNewInventeur("");
              setNewTitulaire("");
              setNewDebiteur("");
              setNewAyantDroit("");
            }}
            sx={{
              color: theme.palette.error.main,
              borderRadius: "10px",
              px: 3,
              py: 1,
              fontWeight: 500,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleAddSubmit}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: "10px",
              px: 4,
              py: 1,
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de modification */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(
              theme.palette.background.paper,
              0.95
            )} 100%)`,
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <SectionTitle>Modifier un Brevet</SectionTitle>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Modifiez les informations du brevet sélectionné
          </Typography>
        </DialogTitle>

        <Divider sx={{ mb: 2 }} />

        <DialogContent sx={{ pt: 2 }}>
          {/* Error Message */}
          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {/* Section: Informations Générales */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📋</span> Informations Générales
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                id="Code_Famille_CEGID_edit"
                label="Code Famille"
                fullWidth
                required
                value={selectedBrevet?.Code_Famille_CEGID || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, Code_Famille_CEGID: e.target.value })}
                // error={!!formErrors.Code_Famille_CEGID}
                // helperText={formErrors.Code_Famille_CEGID || " "}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              <FormControl fullWidth error={!!formErrors.societe_id}>
                <InputLabel id="societe-edit-label">Société *</InputLabel>
                <Select
                  labelId="societe-edit-label"
                  value={selectedBrevet?.societe_id || ""}
                  onChange={(e) => {
                    const societeId = e.target.value;
                    setSelectedBrevet({ ...selectedBrevet, societe_id: societeId, compte_comptable_id: "" });
                    chargerComptesComptables(societeId, true);
                  }} label="Société *"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': { borderRadius: "12px" }
                  }}
                >
                  <MenuItem value="">
                    <em>Choisir une société</em>
                  </MenuItem>
                  {societes.map((soc) => (
                    <MenuItem key={soc.id} value={soc.id}>
                      {soc.nom}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.societe_id && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {formErrors.societe_id}
                  </Typography>
                )}
              </FormControl>

              <TextField
                id="nom"
                label="Nom"
                fullWidth
                required
                value={selectedBrevet?.nom || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, nom: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              {!customCompteComptableEdit ? (
                <FormControl
                  fullWidth
                  disabled={!selectedBrevet?.societe_id || loadingComptesEdit}
                  error={!!formErrors.compte_comptable_id}
                >
                  <InputLabel id="compte-comptable-edit-label">Compte Comptable *</InputLabel>
                  <Select
                    labelId="compte-comptable-edit-label"
                    value={selectedBrevet?.compte_comptable_id || ""}
                    onChange={(e) => setSelectedBrevet({ ...selectedBrevet, compte_comptable_id: e.target.value })}
                    label="Compte Comptable *"
                    sx={{
                      backgroundColor: alpha(theme.palette.background.paper, 0.95),
                      '& .MuiOutlinedInput-root': { borderRadius: "12px" }
                    }}
                  >
                    <MenuItem value="" disabled>
                      {loadingComptesEdit ? "Chargement..." : "Choisir un compte"}
                    </MenuItem>
                    {comptesComptablesEdit.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.code} — {c.libelle}
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomCompteComptableEdit(true);
                        setSelectedBrevet({ ...selectedBrevet, compte_comptable_id: "" });
                      }}
                      sx={{
                        mx: 0.5, borderRadius: '8px',
                        color: '#2a5fa9', fontWeight: 600, fontSize: '0.875rem',
                        display: 'flex', alignItems: 'center', gap: 1,
                      }}
                    >
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #2a5fa9, #4cbed8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: '1.1rem', fontWeight: 700,
                      }}>+</Box>
                      Saisir manuellement
                    </MenuItem>
                  </Select>
                  {formErrors.compte_comptable_id && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formErrors.compte_comptable_id}
                    </Typography>
                  )}
                </FormControl>
              ) : (
                <TextField
                  label="Compte Comptable (saisie manuelle) *"
                  fullWidth
                  autoFocus
                  value={selectedBrevet?.compte_comptable_manuel || ""}
                  onChange={(e) => setSelectedBrevet({ ...selectedBrevet, compte_comptable_manuel: e.target.value })}
                  sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), '& .MuiOutlinedInput-root': { borderRadius: "12px" } }}
                  InputProps={{
                    endAdornment: (
                      <Button
                        size="small"
                        onClick={() => { setCustomCompteComptableEdit(false); setSelectedBrevet({ ...selectedBrevet, compte_comptable_manuel: "" }); }}
                        sx={{ color: "error.main", minWidth: 'auto', fontSize: '0.75rem' }}
                      >
                        ✕
                      </Button>
                    )
                  }}
                />
              )}
              <TextField
                label="Descriptif"
                fullWidth
                multiline
                rows={2}
                value={selectedBrevet?.descriptif || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, descriptif: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              <TextField
                label="Numéro Brevet"
                fullWidth
                value={selectedBrevet?.numero_brevet || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, numero_brevet: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              <TextField
                label="Numéro Dossier"
                fullWidth
                value={selectedBrevet?.numero_dossier || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, numero_dossier: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Parties Prenantes */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>👥</span> Parties Prenantes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Mandataire"
                fullWidth
                value={selectedBrevet?.mandataire || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, mandataire: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              <FormControl fullWidth>
                <InputLabel id="inventeur-label">Inventeur</InputLabel>
                <Select
                  labelId="inventeur-label"
                  value={selectedBrevet?.inventeur || ""}
                  onChange={(e) => setSelectedBrevet({ ...selectedBrevet, inventeur: e.target.value })}
                  label="Inventeur"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Sélectionner un inventeur</em>
                  </MenuItem>
                  {inventeurs.map((inv) => (
                    <MenuItem key={inv} value={inv}>
                      {inv}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Nouvel inventeur"
                  fullWidth
                  value={newInventeur}
                  onChange={(e) => setNewInventeur(e.target.value)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "12px",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (newInventeur.trim() && !inventeurs.includes(newInventeur.trim())) {
                      setInventeurs([...inventeurs, newInventeur.trim()]);
                      setSelectedBrevet({ ...selectedBrevet, inventeur: newInventeur.trim() });
                      setNewInventeur("");
                    }
                  }}
                  disabled={!newInventeur.trim()}
                  sx={{ borderRadius: "12px" }}
                >
                  Ajouter
                </Button>
              </Box>
              <FormControl fullWidth>
                <InputLabel id="titulaire-label">Titulaire</InputLabel>
                <Select
                  labelId="titulaire-label"
                  value={selectedBrevet?.titulaire || ""}
                  onChange={(e) => setSelectedBrevet({ ...selectedBrevet, titulaire: e.target.value })}
                  label="Titulaire"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Sélectionner un titulaire</em>
                  </MenuItem>
                  {titulaires.map((tit) => (
                    <MenuItem key={tit} value={tit}>
                      {tit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Nouveau titulaire"
                  fullWidth
                  value={newTitulaire}
                  onChange={(e) => setNewTitulaire(e.target.value)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "12px",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (newTitulaire.trim() && !titulaires.includes(newTitulaire.trim())) {
                      setTitulaires([...titulaires, newTitulaire.trim()]);
                      setSelectedBrevet({ ...selectedBrevet, titulaire: newTitulaire.trim() });
                      setNewTitulaire("");
                    }
                  }}
                  disabled={!newTitulaire.trim()}
                  sx={{ borderRadius: "12px" }}
                >
                  Ajouter
                </Button>
              </Box>
              <FormControl fullWidth>
                <InputLabel id="debiteur-annuites-label">Débiteur Annuités</InputLabel>
                <Select
                  labelId="debiteur-annuites-label"
                  value={selectedBrevet?.debiteur_annuites || ""}
                  onChange={(e) => setSelectedBrevet({ ...selectedBrevet, debiteur_annuites: e.target.value })}
                  label="Débiteur Annuités"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Sélectionner un débiteur</em>
                  </MenuItem>
                  {debiteurs.map((deb) => (
                    <MenuItem key={deb} value={deb}>
                      {deb}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Nouveau débiteur"
                  fullWidth
                  value={newDebiteur}
                  onChange={(e) => setNewDebiteur(e.target.value)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "12px",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (newDebiteur.trim() && !debiteurs.includes(newDebiteur.trim())) {
                      setDebiteurs([...debiteurs, newDebiteur.trim()]);
                      setSelectedBrevet({ ...selectedBrevet, debiteur_annuites: newDebiteur.trim() });
                      setNewDebiteur("");
                    }
                  }}
                  disabled={!newDebiteur.trim()}
                  sx={{ borderRadius: "12px" }}
                >
                  Ajouter
                </Button>
              </Box>
              <FormControl fullWidth>
                <InputLabel id="ayant-droit-label">Ayant Droit</InputLabel>
                <Select
                  labelId="ayant-droit-label"
                  value={selectedBrevet?.ayant_droit || ""}
                  onChange={(e) => setSelectedBrevet({ ...selectedBrevet, ayant_droit: e.target.value })}
                  label="Ayant Droit"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Sélectionner un ayant droit</em>
                  </MenuItem>
                  {ayantsDroit.map((ayant) => (
                    <MenuItem key={ayant} value={ayant}>
                      {ayant}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Nouvel ayant droit"
                  fullWidth
                  value={newAyantDroit}
                  onChange={(e) => setNewAyantDroit(e.target.value)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "12px",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    if (newAyantDroit.trim() && !ayantsDroit.includes(newAyantDroit.trim())) {
                      setAyantsDroit([...ayantsDroit, newAyantDroit.trim()]);
                      setSelectedBrevet({ ...selectedBrevet, ayant_droit: newAyantDroit.trim() });
                      setNewAyantDroit("");
                    }
                  }}
                  disabled={!newAyantDroit.trim()}
                  sx={{ borderRadius: "12px" }}
                >
                  Ajouter
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Zone de Protection */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🏷️</span> Zone de Protection
            </Typography>
            <FormControl fullWidth margin="dense" error={!!formErrors.pays}>
              <InputLabel id="pays-select-label">Pays *</InputLabel>
              <Select
                id="pays"
                labelId="pays-select-label"
                multiple
                value={selectedBrevet?.pays || []}
                onChange={(event) => setSelectedBrevet({ ...selectedBrevet, pays: event.target.value })}
                input={<OutlinedInput id="select-pays" label="Pays *" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(Array.isArray(selected) ? selected : []).map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px'
                  }
                }}
              >
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.pays && <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>{formErrors.pays}</Typography>}
            </FormControl>
            <Box display="flex" alignItems="center" mt={1}>
              <TextField
                label="Nouveau pays"
                value={newCountry}
                onChange={(e) => setNewCountry(e.target.value)}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  borderRadius: '12px',
                  mr: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px'
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  if (newCountry.trim() && !countries.includes(newCountry.trim().toUpperCase())) {
                    setCountries([...countries, newCountry.trim().toUpperCase()]);
                    setSelectedBrevet({
                      ...selectedBrevet,
                      pays: [...(selectedBrevet?.pays || []), newCountry.trim().toUpperCase()]
                    });
                    setNewCountry("");
                  }
                }}
                disabled={!newCountry.trim()}
                sx={{ borderRadius: '12px' }}
              >
                Ajouter
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Royalties */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>💰</span> Royalties
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControl fullWidth>
                <InputLabel id="type-royalties-label">Type de Royalties</InputLabel>
                <Select
                  labelId="type-royalties-label"
                  value={selectedBrevet?.type_royalties || ""}
                  onChange={(e) => setSelectedBrevet({ ...selectedBrevet, type_royalties: e.target.value })}
                  label="Type de Royalties"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Sélectionner un type</em>
                  </MenuItem>
                  {royaltyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Montant des Royalties"
                type="number"
                fullWidth
                value={selectedBrevet?.montant_royalties || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, montant_royalties: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              <FormControl fullWidth>
                <InputLabel id="frequence-royalties-label">Fréquence des Royalties</InputLabel>
                <Select
                  labelId="frequence-royalties-label"
                  value={selectedBrevet?.frequence_royalties || ""}
                  onChange={(e) => setSelectedBrevet({ ...selectedBrevet, frequence_royalties: e.target.value })}
                  label="Fréquence des Royalties"
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    borderRadius: "12px",
                  }}
                >
                  <MenuItem value="">
                    <em>Sélectionner une fréquence</em>
                  </MenuItem>
                  {royaltyFrequencies.map((freq) => (
                    <MenuItem key={freq} value={freq}>
                      {freq}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Documents */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📄</span> Documents
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <Typography variant="body1">
                  Certificat Dépôt PDF actuel :{" "}
                  {selectedBrevet?.certificat_depot_pdf ? (
                    <a
                      href={`${process.env.REACT_APP_API_URL}/storage/certificats/${selectedBrevet.certificat_depot_pdf.split('/').pop()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedBrevet.certificat_depot_pdf.split('/').pop()}
                    </a>
                  ) : (
                    "Aucun"
                  )}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Laisser vide pour conserver le fichier actuel.
                </Typography>
                <TextField
                  type="file"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: "application/pdf" }}
                  onChange={(e) => setSelectedCertificatDepotFile(e.target.files[0] || null)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "12px",
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body1">
                  Certificat Enregistrement PDF actuel :{" "}
                  {selectedBrevet?.certificat_enregistrement_pdf ? (
                    <a
                      href={`${process.env.REACT_APP_API_URL}/storage/certificats/${selectedBrevet.certificat_enregistrement_pdf.split('/').pop()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedBrevet.certificat_enregistrement_pdf.split('/').pop()}
                    </a>
                  ) : (
                    "Aucun"
                  )}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Laisser vide pour conserver le fichier actuel.
                </Typography>
                <TextField
                  type="file"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ accept: "application/pdf" }}
                  onChange={(e) => setSelectedCertificatEnregistrementFile(e.target.files[0] || null)}
                  sx={{
                    backgroundColor: alpha(theme.palette.background.paper, 0.95),
                    '& .MuiOutlinedInput-root': {
                      borderRadius: "12px",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Dates */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>📅</span> Dates Importantes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                id="date_depot"
                label="Date Dépôt"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={selectedBrevet?.date_depot || ""}
                onChange={handleEditDateDepotChange}
                // error={!!formErrors.date_depot}
                // helperText={formErrors.date_depot || " "}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              <TextField
                label="Date Publication"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedBrevet?.date_publication || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, date_publication: e.target.value })}
                helperText="Auto-calculé : Dépôt + 18 mois"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              <TextField
                label="Date Échéance Annuités"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedBrevet?.date_echeance_annuites || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, date_echeance_annuites: e.target.value })}
                helperText="Anniversaire annuel du dépôt"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
              {/* <TextField
                label="Date Protection"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedBrevet?.date_de_protection || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, date_de_protection: e.target.value })}
                helperText="Égale à la date de dépôt"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              /> */}
              <TextField
                label="Échéance"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={selectedBrevet?.date_expiration || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, date_expiration: e.target.value })}
                helperText="Auto-calculé : Dépôt + 20 ans"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section: Informations Complémentaires */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>ℹ️</span> Informations Complémentaires
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                select
                label="État"
                fullWidth
                value={selectedBrevet?.etat || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, etat: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              >
                {etatsBrevets.map((etat) => (
                  <MenuItem key={etat} value={etat}>
                    {etat}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Commentaires"
                fullWidth
                multiline
                rows={3}
                value={selectedBrevet?.commentaires || ""}
                onChange={(e) => setSelectedBrevet({ ...selectedBrevet, commentaires: e.target.value })}
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "12px",
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider sx={{ mt: 2 }} />

        <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
          <Button
            onClick={() => {
              setOpenEdit(false);
              setSelectedBrevet(null);
              setNewCountry("");
              setNewInventeur("");
              setNewTitulaire("");
              setNewDebiteur("");
              setNewAyantDroit("");
              setSelectedCertificatDepotFile(null);
              setSelectedCertificatEnregistrementFile(null);
            }}
            sx={{
              color: theme.palette.error.main,
              borderRadius: "10px",
              px: 3,
              py: 1,
              fontWeight: 500,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: "10px",
              px: 4,
              py: 1,
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
              },
              transition: "all 0.3s ease",
            }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de suppression */}
      <Dialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        PaperProps={{
          sx: {
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            minWidth: 450,
            px: 2,
            py: 1,
          },
        }}
      >
        <DialogTitle>
          <SectionTitle sx={{ justifyContent: 'center', textAlign: 'center' }}>Supprimer Brevet</SectionTitle>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', textAlign: 'center', mt: 1, fontSize: '0.95rem' }}>
            Voulez-vous vraiment supprimer ce brevet ?
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', textAlign: 'center', mt: 1, fontSize: '0.95rem' }}>
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDelete(false)}
            sx={{ color: theme.palette.error.main, '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) } }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ borderRadius: '8px', background: alpha(theme.palette.error.main, 0.8), '&:hover': { background: theme.palette.error.main, transform: 'scale(1.05)' } }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Brevets;